import { useEffect, useRef, useState } from "react";
import {
  FileText,
  FileSpreadsheet,
  Image as ImageIcon,
  Code,
  File as FileIcon,
} from "lucide-react";
import type { BoundingBox, TableLine, TableRow } from "@/components/pdf-viewer";
import type { ImageBand } from "@/app/recap/types/delineation.types";
import { sourceHover as sourceHoverStore } from "@/app/recap/stores/sourceHoverStore";
import { PreviewContent, type SourceHoverWiring } from "./PreviewContent";

export type { SourceHoverWiring };

const UNPREVIEWABLE_EXTENSIONS = new Set([
  // audio
  "mp3",
  "wav",
  "ogg",
  "m4a",
  "aac",
  "flac",
  "wma",
  "opus",
  // video
  "mp4",
  "mov",
  "avi",
  "mkv",
  "webm",
  "wmv",
  "flv",
  "m4v",
  "3gp",
  "mpeg",
  "mpg",
]);

function isUnpreviewableAttachment(att: NavigableAttachment): boolean {
  const ct = (att.content_type ?? "").toLowerCase();
  if (ct.startsWith("audio/") || ct.startsWith("video/")) return true;
  const ext = att.name.split(".").pop()?.toLowerCase() ?? "";
  return UNPREVIEWABLE_EXTENSIONS.has(ext);
}

function getFileTypeIcon(fileType: string) {
  if (fileType === "pdf") return FileText;
  if (fileType === "image") return ImageIcon;
  if (fileType === "excel") return FileSpreadsheet;
  if (fileType === "html") return Code;
  if (fileType === "text") return FileText;
  return FileIcon;
}

export interface NavigableAttachment {
  name: string;
  content_type?: string | null;
  url?: string | null;
}

interface AttachmentPreviewPanelProps {
  attachmentName: string;
  getFileType: (filename: string) => string;
  documentPdfData: Uint8Array | null;
  documentArrayBuffer: ArrayBuffer | null;
  documentBlobUrl: string | null;
  htmlContent: string | null;
  textContent: string | null;
  previewLoading: boolean;
  previewError: string | null;
  tableRows: TableRow[];
  boundingBoxes?: BoundingBox[];
  tableLines?: TableLine[];
  /** Hovered flow's delineation.rows (1-based physical sheet rows) to highlight in ExcelViewer. */
  highlightedExcelRows?: number[];
  /** Hovered flow's delineation.rows as html element ordinals (STK-1360). */
  highlightedRows?: number[];
  /** Hovered flow's delineation.rows as text line numbers (STK-1360). */
  highlightedTextLines?: number[];
  /** Hovered flow's delineation bounding boxes rendered as full-width bands over a photo attachment. */
  imageBands?: ImageBand[];
  /** Id of the tableRow to bring into view in the PDF viewer (the hovered
   *  flow's first band). The viewer switches to its page and scrolls it into
   *  view when off-screen. Absent → no focus behaviour (STK-1602). */
  focusedRowId?: string | null;
  onClose: () => void;
  onDownload: () => void;
  isDownloading: boolean;
  onRetry?: () => void;
  /** Full attachment list (for navigation tabs). When undefined or single-item, no tabs render. */
  attachments?: NavigableAttachment[];
  /** Callback to switch the active attachment from a tab click. */
  onSelectAttachment?: (att: NavigableAttachment) => void;
  /** Per-order source-rows slice as an Excel buffer, shown behind the
   *  ExcelViewer's "View" toggle (STK-1269). The caller builds it from
   *  `pre_order_metadata.source_rows` (see `recap/utils/sourceRowsToXlsx`).
   *  When provided alongside an Excel attachment, the toggle appears; undefined
   *  → ExcelViewer only, behavior unchanged for all non-dossier / chat uses. */
  excelExtraData?: ArrayBuffer | Uint8Array;
  /** 0-based column in `excelExtraData` carrying each slice row's row number in
   *  the original workbook (`sourceRowsToXlsx`'s `SOURCE_ROW_COLUMN`). Without
   *  it the slice's dense gutter number is looked up as a source ordinal and
   *  rings an unrelated flow (cubic/greptile P1). */
  excelExtraDataSourceRowColumn?: number;
  /** Reverse hover-highlight wiring. Absent ⇒ the panel behaves exactly as
   *  before — the /chat (`EmailMessageDisplay`) and /ai (`SourceDocumentPanelBody`)
   *  callers pass nothing and are out of scope (design §6). */
  sourceHover?: SourceHoverWiring;
}

/**
 * Renders the attachment preview panel (PDF, Excel, Image, HTML).
 * Hosts zoom/rotation state so values persist across attachment-tab switches.
 */
export function AttachmentPreviewPanel({
  attachmentName,
  getFileType,
  documentPdfData,
  documentArrayBuffer,
  documentBlobUrl,
  htmlContent,
  textContent,
  previewLoading,
  previewError,
  tableRows,
  boundingBoxes = [],
  tableLines = [],
  highlightedExcelRows = [],
  highlightedRows = [],
  highlightedTextLines = [],
  imageBands = [],
  focusedRowId = null,
  onClose,
  onDownload,
  isDownloading,
  onRetry,
  attachments,
  onSelectAttachment,
  excelExtraData,
  excelExtraDataSourceRowColumn,
  sourceHover,
}: Readonly<AttachmentPreviewPanelProps>) {
  const fileType = getFileType(attachmentName);

  // Lifted from individual viewers so values persist when the user switches
  // to another attachment via the tabs and comes back.
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);

  // Reveal the resting layer only while the pointer is over the preview body
  // (AC 1/6) — the viewers themselves paint unconditionally whenever their
  // resting prop is non-empty, so this gate is the only thing standing
  // between "always on" and "on hover".
  const [pointerInside, setPointerInside] = useState(false);

  // Closing the panel mid-hover must not leave a row ringed forever — clear
  // the (global, singleton) store on unmount. Gated on ever having WIRED
  // sourceHover: a /chat or /ai instance that never wrote to the store must
  // not be able to clear a sibling instance's live hover (today no two
  // panels ever co-mount, but /chat's EmailMessageDisplay + AIProposalDivider
  // slide-over could in principle, and neither wires sourceHover — the guard
  // costs nothing and removes the reliance on that invariant holding forever).
  // Read via a ref, not the effect's dependency array: the mount-once effect
  // must only clear on true UNMOUNT — depending on `sourceHover` directly
  // would re-fire this cleanup (and clear an active hover) every time the
  // caller's regions recompute while still mounted.
  const wiredSourceHoverRef = useRef(false);
  wiredSourceHoverRef.current = !!sourceHover;
  useEffect(
    () => () => {
      if (wiredSourceHoverRef.current) sourceHoverStore.clear();
    },
    [],
  );

  const navigableAttachments =
    attachments && onSelectAttachment
      ? attachments.filter((a) => !isUnpreviewableAttachment(a))
      : [];

  // Scroll the active tab into view only when the active attachment changes,
  // not on every render (avoids forced scrolling on zoom/rotation updates).
  const activeTabRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    activeTabRef.current?.scrollIntoView({
      inline: "center",
      block: "nearest",
    });
  }, [attachmentName]);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Attachment navigation tabs */}
      {navigableAttachments.length > 1 && (
        <div className="flex items-center gap-1 px-3 py-1.5 border-b bg-gray-50/50 overflow-x-auto flex-shrink-0">
          {navigableAttachments.map((att, idx) => {
            const isActive = att.name === attachmentName;
            const TabIcon = getFileTypeIcon(getFileType(att.name));
            return (
              <button
                key={`${idx}-${att.name}`}
                type="button"
                ref={isActive ? activeTabRef : undefined}
                onClick={() => {
                  if (isActive) return;
                  onSelectAttachment?.(att);
                }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${
                  isActive
                    ? "bg-blue-100 text-blue-700 border border-blue-200"
                    : "text-gray-600 hover:bg-gray-100 border border-transparent"
                }`}
              >
                <TabIcon className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate max-w-[120px]">{att.name}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Preview Content */}
      <div
        className="flex-1 overflow-hidden relative"
        data-testid="attachment-preview-body"
        onMouseEnter={() => setPointerInside(true)}
        onMouseLeave={() => {
          setPointerInside(false);
          sourceHover?.onHover(null);
        }}
      >
        <PreviewContent
          previewLoading={previewLoading}
          previewError={previewError}
          fileType={fileType}
          documentPdfData={documentPdfData}
          documentArrayBuffer={documentArrayBuffer}
          documentBlobUrl={documentBlobUrl}
          htmlContent={htmlContent}
          textContent={textContent}
          boundingBoxes={boundingBoxes}
          tableLines={tableLines}
          tableRows={tableRows}
          highlightedExcelRows={highlightedExcelRows}
          highlightedRows={highlightedRows}
          highlightedTextLines={highlightedTextLines}
          imageBands={imageBands}
          focusedRowId={focusedRowId}
          attachmentName={attachmentName}
          onClose={onClose}
          onDownload={onDownload}
          isDownloading={isDownloading}
          onRetry={onRetry}
          scale={scale}
          rotation={rotation}
          onScaleChange={setScale}
          onRotationChange={setRotation}
          excelExtraData={excelExtraData}
          excelExtraDataSourceRowColumn={excelExtraDataSourceRowColumn}
          sourceHover={sourceHover}
          showRestingBands={pointerInside}
        />
      </div>
    </div>
  );
}
